import { type ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Item, MenuOptionItem, MenuTriggerButton } from "widgets";

import type { Capability, User } from "@/graphql.ts";
import { checkCapability } from "@/modules/users/utils/user.ts";
import { setSampleLocking } from "@/redux/modules/sample.ts";
import { type ITableData } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { CAPABILITY, SAMPLE_LOCKING_ACTIONS } from "@/settings/settings.ts";

interface LockSamplesButtonProps {
  activeTable: ITableData;
}

/**
 * The Lock/Unlock Samples button component
 */
export function LockSamplesButton(props: LockSamplesButtonProps) {
  const dispatch = useDispatch<ReduxDispatch>();
  const user = useSelector<IAppState, User>((state) => state.auth.session);

  // drives the disabled state of the Locking button
  function getLockSamplesDisabledTooltip() {
    if (props.activeTable.selectionModel.selectedIds.length === 0) {
      return "No samples are selected";
    }
    return undefined;
  }

  const buttonDisabledTooltip = getLockSamplesDisabledTooltip();

  const items: ReactElement[] = [];
  if (checkCapability(user.capabilities as Capability[], CAPABILITY.SAMPLE_LOCKING)) {
    items.push(
      <Item key={"lock-samples"}>
        <MenuOptionItem
          id={"lock-samples"}
          icon={"lock"}
          title={`Lock Selected Samples`}
          description={"Prevent future modification"}
        />
      </Item>,
    );
  }
  if (checkCapability(user.capabilities as Capability[], CAPABILITY.SAMPLE_UNLOCKING)) {
    items.push(
      <Item key={"unlock-samples"}>
        <MenuOptionItem
          id={"unlock-samples"}
          icon={"lock_open"}
          title={`Unlock Selected Samples`}
          description={"Allow future modification"}
        />
      </Item>,
    );
  }

  return (
    <MenuTriggerButton
      icon={"lock"}
      label={"Locking"}
      showLabel
      isDisabled={buttonDisabledTooltip}
      key="blast-dialog-popover-btn"
      tooltip={"Change locking state of samples"}
      disabledKeys={undefined}
      onAction={(key) => {
        switch (key) {
          case "lock-samples":
            void dispatch(
              setSampleLocking(
                props.activeTable.selectionModel.selectedIds as number[],
                SAMPLE_LOCKING_ACTIONS.LOCK,
                props.activeTable.tableQuerySpec,
              ),
            );
            break;
          default:
          case "unlock-samples":
            void dispatch(
              setSampleLocking(
                props.activeTable.selectionModel.selectedIds as number[],
                SAMPLE_LOCKING_ACTIONS.UNLOCK,
                props.activeTable.tableQuerySpec,
              ),
            );
            break;
        }
      }}
    >
      {items}
    </MenuTriggerButton>
  );
}
